import React, { useState } from 'react';
import axios from 'axios';
import { Editor as Tiny } from '@tinymce/tinymce-react';
import Preview from './Preview';
import { v4 as uuid } from 'uuid';
import './style.scss';

const Editor = ({ state, setState, index, token, language, user }) => {
  const [show, setShow] = useState({ web: false, mobile: false });

  const HEADERS = {
    headers: {
      Authorization: token,
      'Content-Type': 'multipart/form-data'
    }
  };

  const handleEditorChange = (content, editor) => {
    const array = state.story.content;
    let temporaryarray = array.slice();
    temporaryarray[index]['story_text'] = content;
    setState((previousState) => ({ ...previousState, key: language, temporaryarray }));
  };

  const uploadImage = (blobInfo, success, failure) => {
    let formData = new FormData();
    const id = uuid();
    formData.append('image', blobInfo.blob());
    formData.append('username', user);
    formData.append('title', id);

    axios
      .post('/media', formData, HEADERS)
      .then((res) => success(res.data))
      .catch(console.log);
  };

  const file_picker_callback = (callback, value, meta) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');

    input.onchange = function () {
      const file = this.files[0];

      let formData = new FormData();
      const id = uuid();
      formData.append('image', file);
      formData.append('username', user);
      formData.append('title', id);

      axios
        .post('/media', formData, HEADERS)
        .then((res) => callback(res.data, { title: file.name }))
        .catch(console.log);
    };

    input.click();
  };

  const audio_callback = (data) =>
    `<audio controls>
      <source
        src=${data.source}
        type="${data.sourcemime}"
      />
     </audio>`;

  const video_callback = (data) =>
    `<video
          width="${data.width}"
          height="${data.height}"
          max-width="100%"
          poster="${data.poster || ''}"
          controls="controls"
        >
        <source src="${data.source}" type="${data.sourcemime || ''}"
     </video>`;

  const setup = (editor) => {
    editor.ui.registry.addButton('customPreview', {
      text: 'Preview Mobile',
      onAction: () => setShow({ ...show, mobile: true })
    });

    editor.ui.registry.addButton('customWeb', {
      text: 'Preview Web',
      onAction: () => setShow({ ...show, web: true })
    });
  };

  return (
    <>
      <Tiny
        apiKey={'al0d44iqpq4wbvp5p0fqh8zqrdz2ra9orv2mplu8n0uu8lye'}
        initialValue={state.story?.content[index]?.story_text}
        value={state.story?.content[index]?.story_text}
        init={{
          height: '80vh',
          menubar: 'file edit view insert format tools table help',
          file_picker_types: 'image audio media',
          media_live_embeds: true,
          media_dimensions: false,
          image_caption: true,
          end_container_on_empty_block: true,
          video_template_callback: video_callback,
          audio_template_callback: audio_callback,
          file_picker_callback: file_picker_callback,

          images_upload_handler: uploadImage,
          plugins: `print preview preview paste importcss searchreplace
           autolink autosave save directionality visualblocks visualchars
           fullscreen image link media template table charmap hr
           nonbreaking anchor toc insertdatetime advlist lists
           wordcount imagetools textpattern noneditable help charmap quickbars
           emoticons`,
          toolbar:
            'image undo redo | formatselect | bold italic blockquote backcolor |' +
            'alignleft aligncenter alignright alignjustify |' +
            'bullist numlist outdent indent | removeformat | help | customPreview customWeb',
          setup: setup,
          templates: [
            {
              title: 'Template 1',
              description: 'Story with only text and images.',
              content: `<h1 class="wsite-content-title" style="text-align: center;"><span style="font-family: 'book antiqua', palatino, serif;">Ardclach Bell Tower</span></h1>\n<div class="paragraph" style="text-align: center;"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">Ardclach was the largest parish in Nairnshire.&nbsp; The name means stony high ground in Gaelic and the River Findhorn is the principal feature of the parish.&nbsp; Reverend Mitchell, minister of Ardclach at the end of the eighteenth century described it in the Old Statistical Account:&nbsp;</span></div>\n<div class="paragraph" style="text-align: center;">&nbsp;</div>\n<div>\n<div class="wsite-multicol">\n<blockquote>\n<div class="wsite-multicol-table-wrap"><span style="font-family: 'book antiqua', palatino, serif;">The parish is divided by the Findhorn River, which is not navigable; neither is there a possibility of rendering it so.&nbsp; During the summer months some salmon pass up from the sea, several miles above Ardclach, in this water.&nbsp; No other fish, but trout, is to be found in it.&nbsp; This river is rapid, and frequently impassable every where, excepting Dulcy bridge on the highland road leading from Fort George to Edinburgh.&nbsp; There are two boats on this water within the parish, and one on the post road; but owing either to the mismanagement of the boatmen or temerity of the people, many lives are lost.&nbsp; During the incumbency of the present minister,&nbsp; no less than 23 person have been lost between Dulcy bridge and the post road.&nbsp; The loss of lives from this cause claims attention; and the only method that can be pointed out to prevent it, is to erect a bridge between the two boats, which would render the communication both safe and essay.&nbsp; The parish is a hilly mountainous district, covered with heath, and furnishes little of any other kind of pasture.&nbsp; There is much wood in it, consisting of firs, birch, and what is called weeping birch, alder, hazel, ash and some oaks.&nbsp; The birch, alder and ash, are manufactured for labouring utensils.&nbsp; The wood and hills abound with moor-fowl, wood-cock, partridge, hares, foxes and some deer.&nbsp; The otter and wild cat are frequently seen on the water's side.</span></div>\n</blockquote>\n<div class="wsite-multicol-table-wrap">&nbsp;</div>\n<div class="wsite-multicol-table-wrap">&nbsp;</div>\n</div>\n</div>\n<div class="paragraph" style="text-align: center;"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">One of the things which makes Ardclach so distinctive, however, is its Bell Tower.&nbsp; It is almost unique in Scotland for the Bell Tower of the Parish Kirk sits completely separate from the main church building.&nbsp; The bell tower is high on a hill above the Findhorn valley, the church nestles below it next to the river bank.&nbsp;<br /></span></div>\n<div class="paragraph" style="text-align: center;">&nbsp;</div>\n<div class="paragraph">&nbsp;</div>\n<div>\n<div class="wsite-image wsite-image-border-none "><span style="font-family: 'book antiqua', palatino, serif;"><a><img style="display: block; margin-left: auto; margin-right: auto;" src="http://www.northword.online/uploads/1/2/0/1/120129010/ardclach003_orig.jpg" alt="Picture" /></a></span></div>\n<div class="wsite-image wsite-image-border-none ">&nbsp;</div>\n<div class="wsite-image wsite-image-border-none ">&nbsp;</div>\n</div>\n<div class="paragraph"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">The bell tower dates from the seventeenth century and is a remarkable survival.&nbsp; Nigel Tranter described as "built high above the amphitheatre" of river and church.&nbsp; It was a watch tower for defence, a bell tower and, quite possibly, a prison.&nbsp; It is a square building, beautifully situated and well-maintained with a gabled roof and its appearance almost suggests a tiny tower house.&nbsp; There is a vaulted basement and then a straight stairway up to a main room above it.&nbsp; This room has a fireplace on which the initials MGB (for Margaret Grant Brodie, wife of Brodie of Lethen, the local laird, who constructed the tower in the seventeenth century).&nbsp; There are small windows and shot hole.&nbsp; The belfry itself is on the south wall.</span></div>\n<div class="paragraph"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;"><br /><br />Ardclach Bell Tower is a remarkable little building, almost unknown anywhere else (there only one similar in Caithness).&nbsp; The silence and relative remoteness of the Bell Tower makes a visit into an amazing special experience, where the cares of the world seem very, very far away.&nbsp;&nbsp;</span></div>\n<div class="paragraph">&nbsp;</div>\n<div class="paragraph">&nbsp;</div>\n<div>\n<div class="wsite-image wsite-image-border-none "><span style="font-family: 'book antiqua', palatino, serif;"><a><img style="display: block; margin-left: auto; margin-right: auto;" src="http://www.northword.online/uploads/1/2/0/1/120129010/ardclach002_orig.jpg" alt="Picture" /></a></span></div>\n</div>`
            },
            {
              title: 'Template 2',
              description: 'Story with text, images, video and links to other pages.',
              content: `<h1 class="wsite-content-title" style="text-align: center;"><span style="font-family: 'book antiqua', palatino, serif;">The Burning of the Clavie</span></h1>\n<p style="text-align: center;"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">The Burning of the Clavie, one of northern Scotland's distinctive fire festivals, takes place each year, in Burghead on the Moray Firth coast.&nbsp; It happens on the 11th January and greets the New Year (using the traditional Julian Calendar).</span></p>\n<p style="text-align: center;"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">In September 1752, Great Britain moved from the Julian Calendar to the Gregorian.&nbsp; The 2nd of September 1752 was, therefore, followed by the 14th September, losing the eleven day difference between Julian and Gregorian calendars.&nbsp; People rioted in order to "get back their lost eleven days".&nbsp; However, in some communities, like Burghead (and Foula part of the Shetland Islands) still observe certain winter festival dates according to the Julian calendar.&nbsp; And, so, the burning of the Clavie marks the start of the new year in Burghead.</span></p>\n<p style="text-align: center;">&nbsp;</p>\n<p><span style="font-size: large; font-family: 'book antiqua', palatino, serif;"><img style="display: block; margin-left: auto; margin-right: auto;" src="http://www.northword.online/uploads/1/2/0/1/120129010/clavie004_orig.jpg" alt="Picture" /></span></p>\n<p>&nbsp;</p>\n<p style="text-align: center;"><span style="font-size: large; font-family: 'book antiqua', palatino, serif;">The Clavie is a flaming barrel full of staves and it is carried around the town, followed by a large procession.&nbsp; Eventually, the Clavie is brought to the Doorie Hill on the ramparts of the ancient Pictish Fort.&nbsp; It is wedged in place on Clavie stone and after being refuelled it sits there until it burns itself out and falls down the hill.&nbsp; Its smouldering embers are eagerly awaited by the crowd; a piece of the Clavie is said to bring good luck for the coming year.&nbsp; Indeed pieces of the Clavie are sent around the world to exiled Brochers, as natives of Burghead are called. &nbsp;</span></p>\n<p>&nbsp;</p>\n<table style="border-collapse: collapse; width: 100%;" border="0" cellpadding="0">\n<tbody>\n<tr class="wsite-multicol-tr">\n<td class="wsite-multicol-col" style="width: 91.3502%;">\n<div id="809278421676557067">\n<div>\n<div id="element-e9754c36-3040-4697-addf-e10f5cd63c7e" class="platform-element-contents" data-platform-element-id="848857247979793891-1.0.1">\n<div class="colored-box">\n<div class="colored-box-content">\n<div>\n<div><span style="font-family: 'book antiqua', palatino, serif;">&nbsp;</span></div>\n<div>\n<div class="wsite-multicol">\n<div class="wsite-multicol-table-wrap">\n<table class="wsite-multicol-table" style="width: 93.5811%;">\n<tbody class="wsite-multicol-tbody">\n<tr class="wsite-multicol-tr">\n<td class="wsite-multicol-col" style="width: 100%; text-align: center;"><span style="font-family: 'book antiqua', palatino, serif;">&nbsp;<span style="color: #d5d5d5; font-size: x-large;">&nbsp;Watch this film about the Burning of the Clavie ​from our friends at&nbsp;<a href="https://www.scotsradio.com/" target="_blank" rel="noopener">Scots Radio</a></span></span></td>\n</tr>\n</tbody>\n</table>\n</div>\n</div>\n</div>\n</div>\n</div>\n</div>\n</div>\n</div>\n</div>\n</td>\n<td style="width: 8.6147%; text-align: center;"><span style="font-family: 'book antiqua', palatino, serif;"><a href="https://www.scotsradio.com/"><img src="http://www.northword.online/uploads/1/2/0/1/120129010/published/scotsradio.jpg?1606489217" alt="Picture" /></a></span></td>\n</tr>\n</tbody>\n</table>\n<h2 class="wsite-content-title">&nbsp;</h2>\n<p style="text-align: center;"><span style="font-family: 'book antiqua', palatino, serif;"><span style="font-size: large;"><iframe src="https://www.youtube.com/embed/bp6fjwGKAfg?ab_channel=ScotsRadio" width="560" height="314" allowfullscreen="allowfullscreen"></iframe></span></span></p>\n<div>\n<div class="wsite-multicol">\n<div class="wsite-multicol-table-wrap">&nbsp;</div>\n</div>\n</div>`
            }
          ],
          content_css: false,
          content_style: `

blockquote {
border: 0;
font-size: 12px;
font-weight: 700;
letter-spacing: .1em;
margin: 2.5em auto;
max-width: 540px;
padding: 0 2rem;
position: relative;
text-align: center;
text-transform: uppercase;
}

blockquote::before {
color: #dfe7f2;
content: '“';
background-image: url("../../Assets/Logo.png");
font-family: 'georgia';
font-size: 8em;
left: 30%;
pointer-events: none;
position: absolute;
top: -.75em;
}

blockquote::after {
bottom: -1.2em;
color: #dfe7f2;
content: '”';
font-family: 'georgia';
font-size: 8em;
pointer-events: none;
position: absolute;
right: 30%;
}
`
        }}
        onEditorChange={handleEditorChange}
      />

      <Preview
        show={show.mobile}
        handleCancel={() => setShow({ ...show, mobile: false })}
        index={index}
        state={state}
        title="Mobile Preview"
        size="md"
      />

      <Preview
        show={show.web}
        handleCancel={() => setShow({ ...show, web: false })}
        index={index}
        state={state}
        title="Web Preview"
        size="xl"
      />
    </>
  );
};

export default Editor;
